import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, getMe } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('scs_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Verify token on mount — also honors ?demo_token=<jwt> query param for one-click demo login
  useEffect(() => {
    // Handle demo_token query param (from DemoSwitcher) — swaps the current auth for the demo account
    const params = new URLSearchParams(window.location.search);
    const demoToken = params.get('demo_token');
    if (demoToken) {
      localStorage.setItem('scs_token', demoToken);
      localStorage.removeItem('scs_user');
      // Strip the query param so a browser refresh doesn't re-swap
      const url = new URL(window.location.href);
      url.searchParams.delete('demo_token');
      window.history.replaceState({}, '', url.toString());
    }
    const token = localStorage.getItem('scs_token');
    if (token) {
      getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('scs_user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('scs_token');
          localStorage.removeItem('scs_user');
          setUser(null);
        })
        .finally(() => setInitialized(true));
    } else {
      setInitialized(true);
    }
  }, []);

  const login = useCallback(async (email, password, prefetchedData = null) => {
    setLoading(true);
    try {
      let access_token, userData;
      if (prefetchedData) {
        // OTP flow: token data already returned from verify-otp
        access_token = prefetchedData.access_token;
        userData     = prefetchedData.user;
      } else {
        const res = await apiLogin(email, password);
        access_token = res.data.access_token;
        userData     = res.data.user;
      }
      localStorage.setItem('scs_token', access_token);
      localStorage.setItem('scs_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Login failed' };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('scs_token');
    localStorage.removeItem('scs_user');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initialized, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
